import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { isNativeApp } from '@/core/native';

/* =============================================================================
 * Android back button
 * -----------------------------------------------------------------------------
 * By default Capacitor closes the app on every back press, whatever is on
 * screen. On Android that is wrong and jarring: back means "up one step", and
 * only closes when there is no step left.
 *
 * The order this enforces:
 *   1. an open dialog or drawer closes                (handled by listeners)
 *   2. otherwise go back one page in history
 *   3. at the root, one press warns, a second exits
 *
 * That last part matters on a phone in a pocket — a single accidental press
 * should not kill a shift's tracking session.
 * ========================================================================== */

/** Anything modal registers here so back closes it before it navigates. */
const dismissers: Array<() => boolean> = [];

/**
 * Register a dismisser. Return true from it when it consumed the press.
 * Later registrations are asked first, so the topmost layer wins.
 */
export function registerBackDismiss(fn: () => boolean): () => void {
  dismissers.push(fn);
  return () => {
    const index = dismissers.indexOf(fn);
    if (index >= 0) dismissers.splice(index, 1);
  };
}

export function useAndroidBack(onExitWarning?: () => void): void {
  const navigate = useNavigate();
  const location = useLocation();
  const exitArmed = useRef(false);

  /* What the listener reads, rather than what it closed over.
     ---------------------------------------------------------------------------
     The effect below registers ONCE, for the life of the shell. It cannot
     depend on the path, and that was the whole fault: with location.pathname in
     the dependency array the effect re-ran on every navigation, and
     App.addListener is asynchronous.

     A cleanup that fires before the promise resolves has nothing to remove — so
     on any navigation quicker than the bridge, the old listener stayed
     registered and a new one joined it. Every one of them held a router, a
     location and a component closure, and every one of them fired on the next
     back press: after a dozen screens, one press ran navigate(-1) a dozen
     times, and the handset was carrying a dozen dead component trees it could
     not collect.

     Refs instead. The listener reads the current values at the moment it fires,
     which is when they are wanted, and there is only ever one of it. */
  const current = useRef({ pathname: location.pathname, navigate, onExitWarning });

  /* Written in an effect rather than during render. A ref mutated in the render
     body is a side effect in a function React is entitled to call twice and
     throw away, and the listener below reads this only when somebody presses a
     hardware key — which is always after the paint that updated it. */
  useEffect(() => {
    current.current = { pathname: location.pathname, navigate, onExitWarning };
  });

  useEffect(() => {
    if (!isNativeApp()) return;

    /* Set when the effect is torn down before the bridge hands back a handle.
       Without it the handle arrives after the cleanup has already run and the
       listener lives on with nothing left holding a reference to remove it. */
    let dropped = false;
    let remove: (() => void) | undefined;

    void App.addListener('backButton', () => {
      // Topmost layer first — a dialog over a page must close before the page
      // is navigated away from underneath it.
      for (let i = dismissers.length - 1; i >= 0; i -= 1) {
        if (dismissers[i]?.()) return;
      }

      const { pathname, navigate: go, onExitWarning: warn } = current.current;
      const atRoot = pathname === '/' || pathname === '/login';

      if (!atRoot) {
        go(-1);
        return;
      }

      if (exitArmed.current) {
        void App.exitApp();
        return;
      }

      exitArmed.current = true;
      warn?.();
      window.setTimeout(() => { exitArmed.current = false; }, 2000);
    }).then((handle) => {
      if (dropped) { void handle.remove(); return; }
      remove = () => void handle.remove();
    });

    return () => {
      dropped = true;
      remove?.();
    };
  }, []);
}

/**
 * For modals. Registers a dismisser for as long as `open` is true, so the back
 * button closes it rather than navigating out from under it.
 */
export function useBackToClose(open: boolean, onClose: () => void): void {
  /* Read through a ref so the registration below depends on `open` alone.
     ---------------------------------------------------------------------------
     Most callers write this prop as an inline arrow — `onClose={() =>
     setCreating(false)}` — which is a new identity on every render. With it in
     the dependency array the dismisser was unregistered and re-registered on
     every keystroke in an open form.

     The waste was not the problem. Order was: later registrations are asked
     first, deliberately, so the topmost layer wins. Re-registering moves a
     dismisser to the END of the list — so a form re-rendering underneath an
     open panel would quietly promote itself above it, and one back press would
     close the wrong thing. */
  const latest = useRef(onClose);
  useEffect(() => { latest.current = onClose; });

  useEffect(() => {
    if (!open || !isNativeApp()) return;
    return registerBackDismiss(() => {
      latest.current();
      return true;
    });
  }, [open]);
}
